import * as React from 'react';
import { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useParams } from 'react-router-dom';
import { FluentProvider, webLightTheme } from '@fluentui/react-components';
import { AuthProvider } from './auth/AuthContext';
import { LocationProvider } from './context/LocationContext';
import { Layout, AdminSection, ErrorPage } from './components';
import { LocationFilter } from './config/eventsConfig';

// Add CSS animation for spinner and primary color variable
const spinnerStyle = document.createElement('style');
spinnerStyle.textContent = `
  :root {
    --primary-color: #0c3b5e;
    --primary-hover: #094561;
    --primary-light: rgba(12, 59, 94, 0.1);
    --error-color: #d32f2f;
    --error-light: rgba(211, 47, 47, 0.1);
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(spinnerStyle);

// Location-aware wrapper component
const LocationAwareLayout: React.FC = () => {
  const { location } = useParams<{ location: string }>();
  const [activeSection, setActiveSection] = useState<'home' | 'schedule' | 'photos' | 'faq' | 'rsvp' | 'admin'>('home');
  
  // Check if location parameter is valid
  const validLocations = ['all', 'pune', 'mumbai', 'main', 'reception'];
  
  // If no location parameter is provided, this shouldn't happen due to routing
  // If invalid location parameter is provided, show error page
  if (!location || !validLocations.includes(location)) {
    return <ErrorPage invalidUrl={location || 'unknown'} />;
  }

  const validLocation: LocationFilter = location as LocationFilter;

  if (activeSection === 'admin') {
    return <AdminSection setActiveSection={setActiveSection} />;
  }

  return (
    <LocationProvider location={validLocation}>
      <Layout 
        activeSection={activeSection} 
        setActiveSection={setActiveSection}
      />
    </LocationProvider>
  );
};

// App wrapper that handles routing
const AppWrapper: React.FC = () => {
  return (
    <Router>
      <Routes>
        {/* Root route shows error page */}
        <Route path="/" element={<ErrorPage />} />
        
        {/* Location-based routes */}
        <Route path="/:location" element={<LocationAwareLayout />} />
        
        {/* Catch-all route for unmatched URLs */}
        <Route path="*" element={<ErrorPage />} />
      </Routes>
    </Router>
  );
};

// Main App component with authentication provider
export default function App() {
  return (
    <AuthProvider>
      <FluentProvider theme={webLightTheme}>
        <AppWrapper />
      </FluentProvider>
    </AuthProvider>
  );
}
